import type { TOC } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import { PrismBanner, PrismButton, PrismCard } from '@mylighthouse/prism-ember';
import { ICONS_LOGOS_MAP } from '@mylighthouse/prism-logo';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import { FocusScreen } from 'frontend-utils';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import HotelBenchmarkInsightCompetitorsCompsetBuilderCompsetBuilderHeader from 'lumen/components/hotel/benchmark-insight/competitors/compset-builder/compset-builder-header';
import HotelBenchmarkInsightCompetitorsMapCompetitorsMap from 'lumen/components/hotel/benchmark-insight/competitors/map/competitors-map';
import HotelBenchmarkInsightCompetitorsTableCompetitors from 'lumen/components/hotel/benchmark-insight/competitors/table-competitors';
import type HotelSettingsCompetitorsBenchmarkCompsetBuilderReviewController from 'lumen/controllers/hotel/settings/competitors/benchmark/compset/builder/review';

interface HotelSettingsCompetitorsBenchmarkCompsetBuilderReviewSignature {
  Args: {
    controller: HotelSettingsCompetitorsBenchmarkCompsetBuilderReviewController;
    model: unknown;
  };
}

<template>
  <FocusScreen
    @hasCancel={{true}}
    @isCancelDisabled={{false}}
    @logoHtml={{ICONS_LOGOS_MAP.logo-dark-with-text}}
    @onClose={{@controller.onClose}}
    @size="large"
  >
    <:header>
      <HotelBenchmarkInsightCompetitorsCompsetBuilderCompsetBuilderHeader
        @currentStep={{3}}
      />
    </:header>
    <:body>
      <div class="compset-builder__body compset-builder__body--with-banner">
        <div
          class="compset-builder__body__header bg-elevation-surface flex flex-col gap-400 px-1000 pt-1000 pb-600"
        >
          <h1>{{t
              "settings.competitors.benchmark.compset.builder.review.title"
            }}</h1>

          <PrismBanner
            @bannerMessage={{t
              "settings.competitors.benchmark.compset.builder.review.description"
            }}
            @bannerStyle="info"
            @icon="circle-information-outline"
          />
        </div>

        <div
          class="compset-builder__body__right-sidebar pr-1000 pb-1000 pl-500"
        >
          <div class="relative h-full w-full">
            <div class="absolute right-0 grid h-full w-full">
              {{#unless
                (or
                  @controller.model.proposedCompsets.isRunning
                  @controller.model.desiredCompset.isRunning
                )
              }}
                <HotelBenchmarkInsightCompetitorsMapCompetitorsMap
                  @compset={{@controller.benchmarkInsightCompetitorsCompset.selectedProposedCompetitors}}
                  @showSuggestedCompetitors={{true}}
                />
              {{/unless}}
            </div>
          </div>
        </div>

        <div
          class="compset-builder__body__content flex flex-col gap-400 pr-500 pb-1000 pl-1000"
        >
          <PrismCard
            @contentLayout="noPadding"
            @isSecondary={{true}}
            class="prism-card--with-table-competitors prism-card--with-border h-full"
          >
            <:header as |Header|>
              <div class="text-200--regular flex flex-row items-center gap-200">
                <Header.title>
                  {{t
                    "settings.competitors.benchmark.compset.builder.review.final_compset"
                  }}
                </Header.title>

                {{@controller.formattedNumberOfCompetitors}}
              </div>
            </:header>

            <:content>
              <HotelBenchmarkInsightCompetitorsTableCompetitors
                @competitors={{@controller.benchmarkInsightCompetitorsCompset.selectedProposedCompetitors}}
                @isLoading={{@controller.model.proposedCompsets.isRunning}}
                @isReadOnly={{true}}
                @showSuggestedCompetitorInfo={{true}}
              />
            </:content>
          </PrismCard>

          {{#if @controller.unusedDesiredCompetitors.length}}
            <PrismCard
              @contentLayout="noPadding"
              @isSecondary={{true}}
              class="prism-card--with-table-competitors prism-card--with-border h-full"
            >
              <:header as |Header|>
                <div
                  class="text-200--regular flex flex-row items-center gap-100"
                >
                  <Header.title>
                    {{t
                      "settings.competitors.benchmark.compset.builder.not_used"
                    }}
                  </Header.title>

                  <GeneralIconWithTooltip
                    @size="small"
                    @tooltipContent={{t
                      "settings.competitors.benchmark.compset.builder.not_used.tooltip"
                    }}
                  />
                </div>
              </:header>

              <:content>
                <HotelBenchmarkInsightCompetitorsTableCompetitors
                  {{! @glint-ignore: Class inheritance }}
                  @competitors={{@controller.unusedDesiredCompetitors}}
                  @isLoading={{or
                    @controller.model.proposedCompsets.isRunning
                    @controller.model.desiredCompset.isRunning
                  }}
                  @isReadOnly={{true}}
                  @isRowHoverDisabled={{true}}
                  @showDesiredCompetitorInfoColumn={{true}}
                />
              </:content>
            </PrismCard>
          {{/if}}
        </div>

      </div>
    </:body>
    <:footer>
      <div class="flex w-full flex-row justify-between gap-300">
        <PrismButton
          @buttonType="ghost"
          @iconBefore="arrow-left"
          @label={{t "general.back"}}
          {{on "click" @controller.onClickBack}}
        />

        <PrismButton
          @buttonType="brand"
          @isDisabled={{@controller.benchmarkInsightCompetitorsCompset.saveCompsetTask.isRunning}}
          @isLoading={{@controller.benchmarkInsightCompetitorsCompset.saveCompsetTask.isRunning}}
          @label={{t
            "benchmark_insight.desired_compset.builder.footer.save_compset"
          }}
          {{on "click" @controller.onClickSave}}
        />
      </div>

    </:footer>
  </FocusScreen>
</template> satisfies TOC<HotelSettingsCompetitorsBenchmarkCompsetBuilderReviewSignature>;
